import React, { useCallback, useLayoutEffect, useRef } from 'react';

interface CarouselProps {
  /** Accessible name for the region. */
  label: string;
  children: React.ReactNode[];
  /** Tailwind width classes for each slide (partial next card stays visible on mobile). */
  slideClassName?: string;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Looping carousel. A full copy of the slides sits on each side of the real
 * run, so the cards beside the seam are already there while the row is still
 * moving. Crossing into a copy shifts the scroll position by one run. The
 * cards on screen stay put. Touch uses native overflow scrolling.
 * Arrow keys and the buttons move one slide. No autoplay. Reduced motion
 * jumps instead of smoothing. Clone slides are inert so they are not a second
 * tab stop.
 */
const Carousel: React.FC<CarouselProps> = ({
  label,
  children,
  slideClassName = 'w-[78%] sm:w-[46%] lg:w-[31%]',
}) => {
  const slides = React.Children.toArray(children);
  const count = slides.length;
  const loop = count > 1;
  const trackSlides = loop ? [...slides, ...slides, ...slides] : slides;

  const trackRef = useRef<HTMLDivElement>(null);
  const jumping = useRef(false);
  const settling = useRef(false);

  const metrics = useCallback(() => {
    const el = trackRef.current;
    if (!el) return null;
    const nodes = [...el.querySelectorAll<HTMLElement>('[data-slide]')];
    if (nodes.length === 0) return null;
    const pad = parseFloat(getComputedStyle(el).paddingLeft) || 0;
    return { el, nodes, pad };
  }, []);

  /** Scroll position that puts this slide's left edge on the track's content edge. */
  const alignLeft = (el: HTMLElement, slide: HTMLElement, pad: number) =>
    el.scrollLeft + slide.getBoundingClientRect().left - el.getBoundingClientRect().left - pad;

  const nearestIndex = useCallback(() => {
    const m = metrics();
    if (!m) return 0;
    const edge = m.el.getBoundingClientRect().left + m.pad;
    let best = 0;
    let bestDist = Infinity;
    m.nodes.forEach((node, i) => {
      const dist = Math.abs(node.getBoundingClientRect().left - edge);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    return best;
  }, [metrics]);

  const shiftBy = useCallback((delta: number) => {
    const el = trackRef.current;
    if (!el || jumping.current || delta === 0) return;
    jumping.current = true;
    el.style.scrollSnapType = 'none';
    el.scrollLeft += delta;
    let ignoreEnd = true;
    const restore = () => {
      if (ignoreEnd) return;
      el.style.scrollSnapType = '';
      el.removeEventListener('scrollend', restore);
    };
    el.addEventListener('scrollend', restore);
    requestAnimationFrame(() => {
      ignoreEnd = false;
      const edge = el.getBoundingClientRect().left + (parseFloat(getComputedStyle(el).paddingLeft) || 0);
      const nodes = [...el.querySelectorAll<HTMLElement>('[data-slide]')];
      const aligned = nodes.some((node) => Math.abs(node.getBoundingClientRect().left - edge) <= 2);
      if (aligned) restore();
      jumping.current = false;
    });
  }, []);

  const normalize = useCallback(() => {
    if (!loop || jumping.current) return;
    const m = metrics();
    if (!m || m.nodes.length < count * 3) return;
    const shift = alignLeft(m.el, m.nodes[count], m.pad) - alignLeft(m.el, m.nodes[0], m.pad);
    if (shift < 8) return;
    const index = nearestIndex();
    if (index < count) shiftBy(shift);
    else if (index >= count * 2) shiftBy(-shift);
  }, [loop, count, metrics, nearestIndex, shiftBy]);

  useLayoutEffect(() => {
    if (!loop) return;
    const m = metrics();
    if (!m?.nodes[count]) return;
    jumping.current = true;
    const el = m.el;
    const previousSnap = el.style.scrollSnapType;
    el.style.scrollSnapType = 'none';
    el.scrollLeft = Math.max(0, alignLeft(el, m.nodes[count], m.pad));
    let frame = requestAnimationFrame(() => {
      el.style.scrollSnapType = previousSnap;
      frame = requestAnimationFrame(() => {
        jumping.current = false;
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [loop, count, metrics]);

  const onScroll = () => {
    if (jumping.current || settling.current) return;
    normalize();
  };

  const go = (dir: 1 | -1) => {
    const m = metrics();
    if (!m || count === 0) return;
    const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
    if (!loop) {
      m.el.scrollBy({ left: dir * m.el.clientWidth * 0.8, behavior });
      return;
    }
    const index = nearestIndex();
    const next = m.nodes[index + dir];
    if (!next) return;
    const left = Math.max(0, alignLeft(m.el, next, m.pad));
    settling.current = true;
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      settling.current = false;
      normalize();
    };
    m.el.addEventListener('scrollend', finish, { once: true });
    window.setTimeout(finish, behavior === 'auto' ? 50 : 700);
    m.el.scrollTo({ left, behavior });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      go(1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      go(-1);
    }
  };

  const btn =
    'w-11 h-11 rounded-full border border-secondary-700 bg-secondary-900/90 text-secondary-200 flex items-center justify-center transition-colors enabled:hover:text-primary-300 enabled:hover:border-primary-500/50 disabled:opacity-35 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400';

  return (
    <div role="region" aria-roledescription="carousel" aria-label={label}>
      <div
        ref={trackRef}
        onScroll={onScroll}
        onKeyDown={onKeyDown}
        tabIndex={0}
        aria-label={`${label} (use arrow keys or swipe)`}
        className="flex gap-3 sm:gap-4 overflow-x-auto motion-reduce:scroll-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden overscroll-x-contain rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
      >
        {trackSlides.map((child, i) => {
          const clone = loop && (i < count || i >= count * 2);
          return (
            <Slide key={i} clone={clone} className={`shrink-0 ${slideClassName}`}>
              {child}
            </Slide>
          );
        })}
      </div>
      <div className="flex items-center justify-end gap-2 mt-2">
        <button type="button" onClick={() => go(-1)} disabled={count < 2} className={btn} aria-label={`Previous ${label}`}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        </button>
        <button type="button" onClick={() => go(1)} disabled={count < 2} className={btn} aria-label={`Next ${label}`}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        </button>
      </div>
    </div>
  );
};

const Slide: React.FC<{ clone: boolean; className: string; children: React.ReactNode }> = ({ clone, className, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.inert = clone;
  }, [clone]);
  return (
    <div ref={ref} data-slide className={className} aria-hidden={clone || undefined}>
      {children}
    </div>
  );
};

export default Carousel;
